import React, { useEffect, useMemo, useState } from 'react';
import { arrayOf, bool, node, number, oneOfType, shape, string } from 'prop-types';
import {
  Box,
  Chip,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { NoAvailableCard } from 'common';
import palette from '../../../../../../library/theme/palette';
import { compareProductIdns, getProductId, getProductRow } from './productRules';

const renderTooltipCell = ({ value }) => (
  <Tooltip arrow placement="bottom" title={value || ''}>
    <span
      style={{
        display: 'inline-block',
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        verticalAlign: 'bottom',
        whiteSpace: 'nowrap',
      }}
    >
      {value}
    </span>
  </Tooltip>
);

const ProductsGrid = ({
  aboveGridContent,
  belowQuickFilterContent,
  darkHeader,
  extraColumns,
  framed,
  gridHeight,
  gridProps,
  hideGrid,
  hideQuickFilter,
  newProductIds,
  panelTitle,
  products,
  quickFilterAriaLabel,
  quickFilterLabel,
  quickFilterPlaceholder,
  quickFilterPlacement,
  showSelectionHeaderCheckbox,
  topRightContent,
}) => {
  const [quickFilterInput, setQuickFilterInput] = useState('');
  const [quickFilterValues, setQuickFilterValues] = useState([]);
  const [sortModel, setSortModel] = useState([]);

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      setQuickFilterValues(
        quickFilterInput
          .split(' ')
          .map(value => value.trim())
          .filter(Boolean)
      );
    }, 200);
    return () => clearTimeout(debounceTimeout);
  }, [quickFilterInput]);

  const rows = useMemo(
    () =>
      products
        .map(product => ({
          ...product,
          ...getProductRow(product),
          isNew: newProductIds.includes(getProductId(product)),
        }))
        .sort((left, right) => compareProductIdns(left.productIdn, right.productIdn)),
    [newProductIds, products]
  );

  const columns = useMemo(
    () => [
      {
        align: 'left',
        field: 'productIdn',
        flex: 0.3,
        headerAlign: 'left',
        headerName: 'ID',
        minWidth: 70,
        renderCell: renderTooltipCell,
        resizable: true,
        sortComparator: compareProductIdns,
      },
      {
        align: 'left',
        field: 'productName',
        flex: 1,
        headerAlign: 'left',
        headerName: 'Product Name',
        minWidth: 160,
        renderCell: ({ row, value }) => (
          <Box
            sx={{ alignItems: 'center', display: 'flex', gap: 1, maxWidth: '100%', minWidth: 0 }}
          >
            {renderTooltipCell({ value })}
            {row.isNew && (
              <Chip
                label="New"
                size="small"
                sx={{
                  backgroundColor: '#FCE7D6',
                  borderRadius: '3px',
                  color: '#E86A00',
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  height: 24,
                  textTransform: 'uppercase',
                }}
              />
            )}
          </Box>
        ),
        resizable: true,
      },
      {
        align: 'left',
        field: 'pidx',
        flex: 0.35,
        headerAlign: 'left',
        headerName: 'PIDX',
        minWidth: 80,
        renderCell: renderTooltipCell,
        resizable: true,
      },
      ...extraColumns,
    ],
    [extraColumns]
  );

  const filterModel = useMemo(() => ({ items: [], quickFilterValues }), [quickFilterValues]);
  const quickFilter = (
    <TextField
      fullWidth
      onChange={event => setQuickFilterInput(event.target.value)}
      placeholder={quickFilterPlaceholder}
      size="small"
      slotProps={{
        input: {
          'aria-label': quickFilterAriaLabel || quickFilterLabel || 'Filter products',
          endAdornment: quickFilterInput ? (
            <InputAdornment position="end">
              <IconButton
                aria-label="clear search"
                edge="end"
                onClick={() => setQuickFilterInput('')}
                size="small"
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" fontSize="small" />
            </InputAdornment>
          ),
        },
      }}
      value={quickFilterInput}
      variant="outlined"
    />
  );

  return (
    <Box sx={{ width: '100%' }}>
      {!hideQuickFilter && quickFilterPlacement === 'top' && (
        <Box
          sx={{
            alignItems: { lg: 'center', xs: 'stretch' },
            backgroundColor: 'common.white',
            display: 'flex',
            flexDirection: { lg: 'row', xs: 'column' },
            gap: 1.25,
            pb: 1.5,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>{quickFilter}</Box>
          {topRightContent}
        </Box>
      )}
      {belowQuickFilterContent}
      {aboveGridContent}
      <Box
        sx={
          framed
            ? {
                backgroundColor: 'common.white',
                border: '1px solid',
                borderColor: 'grey.200',
                borderRadius: 1,
                overflow: 'hidden',
              }
            : {
                backgroundColor: 'common.white',
                overflow: 'hidden',
              }
        }
      >
        {!hideQuickFilter && quickFilterPlacement === 'header' && (
          <Box
            sx={{
              alignItems: 'center',
              borderBottom: '1px solid',
              borderColor: 'grey.200',
              color: 'text.primary',
              display: 'flex',
              gap: 2,
              justifyContent: 'space-between',
              px: 1.5,
              py: 1,
            }}
          >
            <Typography color="inherit" fontWeight={700} variant="body2">
              {panelTitle}
            </Typography>
            <Box sx={{ maxWidth: 240, width: '100%' }}>{quickFilter}</Box>
          </Box>
        )}
        {!hideGrid && (
          <Box sx={{ height: gridHeight || 'auto' }}>
            <DataGridPremium
              autoHeight={!gridHeight}
              columns={columns}
              disableColumnFilter
              disableColumnMenu
              disableColumnSelector
              disableDensitySelector
              disableRowSelectionOnClick
              filterModel={filterModel}
              getRowClassName={({ row }) => (row.isNew ? 'new-product-row' : '')}
              hideFooter
              onSortModelChange={setSortModel}
              rows={rows}
              slotProps={{
                baseCheckbox: {
                  size: 'small',
                },
              }}
              slots={{
                noRowsOverlay: () => (
                  <NoAvailableCard
                    suggestions="Try adjusting your search or filter options."
                    type="products"
                  />
                ),
              }}
              sortingOrder={['asc', 'desc', null]}
              sortModel={sortModel}
              sx={{
                '& .MuiDataGrid-cell': {
                  alignItems: 'center',
                },
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: darkHeader ? palette._next.table.header : 'transparent',
                },
                ...(!showSelectionHeaderCheckbox && {
                  '& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer': {
                    display: 'none',
                  },
                }),
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: darkHeader ? palette._next.surface.white : 'text.primary',
                  fontWeight: 700,
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: darkHeader ? palette._next.table.header : 'grey.50',
                  borderBottom: '1px solid',
                  borderColor: 'grey.200',
                },
                '& .MuiDataGrid-filler': {
                  backgroundColor: darkHeader ? palette._next.table.header : 'transparent',
                },
                '& .MuiDataGrid-footerContainer': {
                  display: 'none',
                },
                '& .MuiDataGrid-iconButtonContainer .MuiSvgIcon-root': {
                  color: darkHeader ? palette._next.surface.white : 'text.secondary',
                },
                '& .MuiDataGrid-row': {
                  backgroundColor: 'common.white',
                },
                '& .MuiDataGrid-row.new-product-row .MuiDataGrid-cell': {
                  backgroundColor: '#FFF7F1',
                },
                '& .MuiDataGrid-scrollbarFiller': {
                  backgroundColor: darkHeader ? palette._next.table.header : 'transparent',
                },
                border: 0,
              }}
              {...gridProps}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

ProductsGrid.propTypes = {
  aboveGridContent: node,
  belowQuickFilterContent: node,
  darkHeader: bool,
  extraColumns: arrayOf(shape({})),
  framed: bool,
  gridHeight: oneOfType([number, string]),
  gridProps: shape({}),
  hideGrid: bool,
  hideQuickFilter: bool,
  newProductIds: arrayOf(oneOfType([number, string])),
  panelTitle: string,
  products: arrayOf(shape({})),
  quickFilterAriaLabel: string,
  quickFilterLabel: string,
  quickFilterPlaceholder: string,
  quickFilterPlacement: string,
  showSelectionHeaderCheckbox: bool,
  topRightContent: node,
};

ProductsGrid.defaultProps = {
  aboveGridContent: null,
  belowQuickFilterContent: null,
  darkHeader: false,
  extraColumns: [],
  framed: true,
  gridHeight: null,
  gridProps: {},
  hideGrid: false,
  hideQuickFilter: false,
  newProductIds: [],
  panelTitle: '',
  products: [],
  quickFilterAriaLabel: 'Filter products',
  quickFilterLabel: 'Filter products',
  quickFilterPlaceholder: 'Filter products...',
  quickFilterPlacement: 'top',
  showSelectionHeaderCheckbox: false,
  topRightContent: null,
};

export default ProductsGrid;
